import React, {useCallback, useEffect, useState} from 'react';
import createAxiosInstance from '../support/AxiosSupport';
import {fetchRef} from '../service/refsCache';
import {invalidatePreferencesCache} from '../service/preferencesCache';
import Spinner from './Spinner';
import {useToast} from '../context/ToastContext';

/**
 * Раздел «Предпочтения для AI» в настройках профиля.
 *
 * Контракт:
 *   GET  /profile/settings/preferences → UserPreferencesDto
 *   PUT  /profile/settings/preferences ← UpdateUserPreferencesDto
 *   GET  /refs/styles                  → RefDto[]  (используется fetchRef)
 */

interface RefDto {
    id: string;
    code: string;
    nameRu: string;
    nameEn?: string;
}

interface UserPreferencesDto {
    aiStyleNote?: string | null;
    gender?: string | null;
    ageYears?: number | null;
    heightCm?: number | null;
    weightKg?: number | null;
    itemAdviceMode?: string | null;
    formalStandard?: string | null;
    preferredStyles?: RefDto[];
    oneInOneOutEnabled?: boolean | null;
}

interface UpdateUserPreferencesDto {
    aiStyleNote?: string | null;
    gender?: string | null;
    ageYears?: number | null;
    heightCm?: number | null;
    weightKg?: number | null;
    itemAdviceMode?: string | null;
    formalStandard?: string | null;
    preferredStyleIds?: string[];
    oneInOneOutEnabled?: boolean;
}

const GENDER_OPTIONS = [
    {value: '', label: 'Не указан'},
    {value: 'MALE', label: 'Мужской'},
    {value: 'FEMALE', label: 'Женский'},
    {value: 'NON_BINARY', label: 'Небинарный'},
    {value: 'PREFER_NOT_TO_SAY', label: 'Предпочитаю не указывать'},
];

const ITEM_ADVICE_OPTIONS: {value: string; label: string; hint: string}[] = [
    {value: 'STRICT', label: 'Строгий', hint: 'Сразу утилизировать изношенные'},
    {value: 'MODERATE', label: 'Умеренный', hint: 'По ситуации'},
    {value: 'LENIENT', label: 'Мягкий', hint: 'Предпочитать домашнее использование'},
];

const FORMAL_STANDARD_OPTIONS: {value: string; label: string}[] = [
    {value: 'PERFECT_ONLY', label: 'Только идеальное состояние'},
    {value: 'GOOD_PLUS', label: 'Хорошее и лучше'},
    {value: 'FAIR_OK', label: 'Удовлетворительное тоже ок'},
];

const UserPreferencesSection: React.FC = () => {
    const {showToast} = useToast();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);

    // Form state
    const [aiStyleNote, setAiStyleNote] = useState('');
    const [gender, setGender] = useState('');
    const [ageYears, setAgeYears] = useState('');
    const [heightCm, setHeightCm] = useState('');
    const [weightKg, setWeightKg] = useState('');
    const [itemAdviceMode, setItemAdviceMode] = useState('');
    const [formalStandard, setFormalStandard] = useState('');
    const [preferredStyleIds, setPreferredStyleIds] = useState<string[]>([]);
    const [oneInOneOut, setOneInOneOut] = useState(false);

    // Reference data
    const [styles, setStyles] = useState<RefDto[]>([]);

    const loadData = useCallback(async () => {
        setLoading(true);
        setLoadError(null);
        try {
            const [api, stylesData] = await Promise.all([
                createAxiosInstance(),
                fetchRef<RefDto[]>('styles'),
            ]);
            setStyles(stylesData);

            const res = await api.get<UserPreferencesDto>('/profile/settings/preferences');
            const p = res.data;
            setAiStyleNote(p.aiStyleNote ?? '');
            setGender(p.gender ?? '');
            setAgeYears(p.ageYears != null ? String(p.ageYears) : '');
            setHeightCm(p.heightCm != null ? String(p.heightCm) : '');
            setWeightKg(p.weightKg != null ? String(p.weightKg) : '');
            setItemAdviceMode(p.itemAdviceMode ?? '');
            setFormalStandard(p.formalStandard ?? '');
            setPreferredStyleIds((p.preferredStyles ?? []).map((s) => s.id));
            setOneInOneOut(p.oneInOneOutEnabled === true);
        } catch (err) {
            setLoadError('Не удалось загрузить настройки предпочтений');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const toggleStyle = useCallback((id: string) => {
        setPreferredStyleIds((prev) =>
            prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
        );
    }, []);

    const handleSubmit = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        try {
            const api = await createAxiosInstance();
            const payload: UpdateUserPreferencesDto = {
                aiStyleNote: aiStyleNote.trim() || null,
                gender: gender || null,
                ageYears: ageYears ? parseInt(ageYears, 10) : null,
                heightCm: heightCm ? parseInt(heightCm, 10) : null,
                weightKg: weightKg ? parseFloat(weightKg) : null,
                itemAdviceMode: itemAdviceMode || null,
                formalStandard: formalStandard || null,
                preferredStyleIds: preferredStyleIds.length ? preferredStyleIds : [],
                oneInOneOutEnabled: oneInOneOut,
            };
            await api.put('/profile/settings/preferences', payload);
            invalidatePreferencesCache();
            showToast('Предпочтения сохранены', 'success');
        } catch (err) {
            showToast('Не удалось сохранить предпочтения', 'error');
        } finally {
            setSaving(false);
        }
    }, [aiStyleNote, gender, ageYears, heightCm, weightKg, itemAdviceMode, formalStandard, preferredStyleIds, oneInOneOut, showToast]);

    if (loading) {
        return (
            <div className="card p-6 mt-6 flex justify-center py-12">
                <Spinner/>
            </div>
        );
    }

    if (loadError) {
        return (
            <div className="card p-6 mt-6">
                <p className="text-sm text-red-600 dark:text-red-400 mb-3">{loadError}</p>
                <button onClick={loadData} className="btn-secondary">
                    Повторить
                </button>
            </div>
        );
    }

    return (
        <div className="card p-6 mt-6" data-testid="user-preferences-section">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-5">
                Предпочтения для AI
            </h3>
            <form onSubmit={handleSubmit} className="space-y-6">

                {/* Описание для AI */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Описание для AI
                    </label>
                    <textarea
                        value={aiStyleNote}
                        onChange={(e) => setAiStyleNote(e.target.value)}
                        rows={4}
                        className="input-field min-h-[100px] resize-y"
                        placeholder="Расскажите о себе: стиль жизни, что важно при выборе одежды, любимые сочетания"
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Расскажите о себе: стиль жизни, что важно при выборе одежды, любимые сочетания
                    </p>
                </div>

                {/* Личные данные */}
                <div>
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                        Личные данные
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div>
                            <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                                Пол
                            </label>
                            <select
                                value={gender}
                                onChange={(e) => setGender(e.target.value)}
                                className="input-field"
                            >
                                {GENDER_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                                Возраст
                            </label>
                            <input
                                type="number"
                                min={1}
                                max={120}
                                step={1}
                                value={ageYears}
                                onChange={(e) => setAgeYears(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
                                className="input-field"
                                placeholder="—"
                            />
                        </div>
                        <div>
                            <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                                Рост, см
                            </label>
                            <input
                                type="number"
                                min={50}
                                max={280}
                                step={1}
                                value={heightCm}
                                onChange={(e) => setHeightCm(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
                                className="input-field"
                                placeholder="—"
                            />
                        </div>
                        <div>
                            <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                                Вес, кг
                            </label>
                            <input
                                type="number"
                                min={1}
                                max={500}
                                step={0.1}
                                value={weightKg}
                                onChange={(e) => setWeightKg(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
                                className="input-field"
                                placeholder="—"
                            />
                        </div>
                    </div>
                </div>

                {/* Порог совета по утилизации */}
                <div>
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                        Порог совета по утилизации
                    </p>
                    <div className="flex flex-col gap-2">
                        {ITEM_ADVICE_OPTIONS.map((opt) => (
                            <label
                                key={opt.value}
                                className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                                    itemAdviceMode === opt.value
                                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                                        : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                                }`}
                            >
                                <input
                                    type="radio"
                                    name="itemAdviceMode"
                                    value={opt.value}
                                    checked={itemAdviceMode === opt.value}
                                    onChange={() => setItemAdviceMode(opt.value)}
                                    className="mt-0.5 accent-primary-600"
                                />
                                <div>
                                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                        {opt.label}
                                    </span>
                                    <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                                        — {opt.hint}
                                    </span>
                                </div>
                            </label>
                        ))}
                        <label
                            className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                                itemAdviceMode === ''
                                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                                    : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                            }`}
                        >
                            <input
                                type="radio"
                                name="itemAdviceMode"
                                value=""
                                checked={itemAdviceMode === ''}
                                onChange={() => setItemAdviceMode('')}
                                className="mt-0.5 accent-primary-600"
                            />
                            <span className="text-sm text-gray-600 dark:text-gray-400">Не указано</span>
                        </label>
                    </div>
                </div>

                {/* Стандарт для торжественных выходов */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Стандарт для торжественных выходов
                    </label>
                    <select
                        value={formalStandard}
                        onChange={(e) => setFormalStandard(e.target.value)}
                        className="input-field"
                    >
                        <option value="">Не указано</option>
                        {FORMAL_STANDARD_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>

                {/* Предпочитаемые стили */}
                <div>
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Предпочитаемые стили
                    </p>
                    {styles.length === 0 ? (
                        <p className="text-sm text-gray-500 dark:text-gray-400">Нет доступных стилей</p>
                    ) : (
                        <div className="flex flex-wrap gap-2">
                            {styles.map((s) => (
                                <button
                                    key={s.id}
                                    type="button"
                                    onClick={() => toggleStyle(s.id)}
                                    className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                                        preferredStyleIds.includes(s.id)
                                            ? 'bg-primary-100 dark:bg-primary-900 border-primary-500 text-primary-700 dark:text-primary-300'
                                            : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                                    }`}
                                >
                                    {s.nameRu}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* Режим «Одно пришло — одно ушло» */}
                <div className="flex items-start justify-between gap-4 min-h-[44px]">
                    <div>
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Одно пришло — одно ушло
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 max-w-lg">
                            После добавления новой вещи предлагать выбрать, какую старую отправить
                            на стадию «Ждут передачи» (обратимо). Шаг всегда можно пропустить.
                        </p>
                    </div>
                    <button
                        type="button"
                        role="switch"
                        aria-checked={oneInOneOut}
                        aria-label="Одно пришло — одно ушло"
                        data-testid="one-in-one-out-toggle"
                        onClick={() => setOneInOneOut((v) => !v)}
                        className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                            oneInOneOut ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-600'
                        }`}
                    >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${oneInOneOut ? 'translate-x-6' : 'translate-x-1'}`}/>
                    </button>
                </div>

                <div className="pt-2 flex justify-end">
                    <button
                        type="submit"
                        disabled={saving}
                        className="btn-primary disabled:opacity-50"
                    >
                        {saving ? 'Сохранение...' : 'Сохранить'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default UserPreferencesSection;
